import { constants } from 'appConstants';
import { useEffect, useMemo } from 'react';
import { useChatContext } from '@ltvco/refresh-lib/v2';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { MessagesContainer } from './components/MessagesContainer';
import { useLocation } from 'react-router-dom';
import { RecentReports } from 'routes/Dashboard/RecentReports/RecentReports';
import { ChatPageLayout } from './ChatPageLayout';
import { DamageAnalyzerPage } from '../../PhotoDamageAnalyzer/DamageAnalyzerPage';
import { ValueForecasterPage } from '../../ValueForecaster/ValueForecasterPage';
import { RepairCostEstimatorPage } from '../../RepairCostEstimator/RepairCostEstimatorPage';

type FeatureName =
  | 'createSmartSearchBot'
  | 'createVehiclePhotoDamageAnalyzerBot'
  | 'createVehicleValueForecasterBot'
  | 'createRepairCostEstimatorBot'
  | 'createVehicleMatchmakerBot';

const ChatPageContent = () => {
  const location = useLocation();
  const reportLibraryRoute = `${constants.links.chatAILink}/library`;
  const isReportLibraryOpen = location.pathname === reportLibraryRoute;
  const photoDamageRoute = `${constants.links.chatAILink}/photo-damage-analyzer`;
  const valueForecasterRoute = `${constants.links.chatAILink}/value-forecaster`;
  const repairCostEstimatorRoute = `${constants.links.chatAILink}/repair-cost-estimator`;
  const vehicleMatchmakerRoute = `${constants.links.chatAILink}/vehicle-matchmaker`;
  const isPhotoDamageRoute = location.pathname === photoDamageRoute;
  const isValueForecasterRoute = location.pathname === valueForecasterRoute;
  const isRepairCostEstimatorRoute =
    location.pathname === repairCostEstimatorRoute;

  const {
    messages,
    loadingStatus,
    showLanding,
    isLogoAnimating,
    logoAnimationPhase,
    shouldCloseReport,
    isReportOpen,
    setIsReportOpen,
    handleShouldCloseReport,
    sendMessage,
    handleFeatureNameChange,
  } = useChatContext();
  const { isMobile } = useScreenSize();
  const featureName: FeatureName = useMemo(() => {
    let featureName: FeatureName = 'createSmartSearchBot';

    if (isRepairCostEstimatorRoute)
      featureName = 'createRepairCostEstimatorBot';
    else if (isValueForecasterRoute)
      featureName = 'createVehicleValueForecasterBot';
    else if (isPhotoDamageRoute)
      featureName = 'createVehiclePhotoDamageAnalyzerBot';
    return featureName;
  }, [isPhotoDamageRoute, isValueForecasterRoute, isRepairCostEstimatorRoute]);

  // Only sync when featureName changes
  useEffect(() => {
    handleFeatureNameChange(featureName);
  }, [handleFeatureNameChange, featureName]);

  useEffect(() => {
    document.body.classList.add('chat-page');

    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  if (isReportLibraryOpen) {
    return <RecentReports isInChatPage />;
  }

  if (isPhotoDamageRoute) {
    return <DamageAnalyzerPage />;
  }

  if (isValueForecasterRoute) {
    return <ValueForecasterPage />;
  }

  if (isRepairCostEstimatorRoute) {
    return <RepairCostEstimatorPage />;
  }

  return (
    <MessagesContainer
      messages={messages}
      loadingStatus={loadingStatus}
      onReportStateChange={setIsReportOpen}
      shouldCloseReport={shouldCloseReport}
      onReportCloseHandled={handleShouldCloseReport}
      showLanding={showLanding}
      isLogoAnimating={isLogoAnimating}
      logoAnimationPhase={logoAnimationPhase}
      onSendMessage={sendMessage}
      sx={{
        ...(isMobile &&
          !isReportOpen && {
            paddingTop: '0',
          }),
      }}
    />
  );
};

export const ChatPage = () => {
  return (
    <ChatPageLayout showChatInput>
      <ChatPageContent />
    </ChatPageLayout>
  );
};
